import React, { useState, useEffect } from 'react';
import AdminNavbar from '../components/AdminNavbar';
import { fetchSettings, updateSettings } from '../utils/settingsApi';
import { uploadImageToR2 } from '../utils/s3Upload';
import './AdminGallery.css';

export default function AdminSettings() {
    const [settings, setSettings] = useState({
        addresses: '',
        emails: '',
        phones: '',
        facebook: '',
        instagram: '',
        linkedin: ''
    });
    
    const [announcementSettings, setAnnouncementSettings] = useState({
        announcement_active: 'false',
        announcement_title: '',
        announcement_text: '',
        announcement_image_url: '',
        announcement_id: ''
    });

    const [imageFile, setImageFile] = useState(null);
    
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState({ type: '', text: '' });

    useEffect(() => {
        const loadSettings = async () => {
            const data = await fetchSettings();
            setSettings({
                addresses: data.addresses || '',
                emails: data.emails || '',
                phones: data.phones || '',
                facebook: data.facebook || '',
                instagram: data.instagram || '',
                linkedin: data.linkedin || ''
            });

            setAnnouncementSettings({
                announcement_active: data.announcement_active || 'false',
                announcement_title: data.announcement_title || '',
                announcement_text: data.announcement_text || '',
                announcement_image_url: data.announcement_image_url || '',
                announcement_id: data.announcement_id || ''
            });
        };
        loadSettings();
    }, []);

    const handleChange = (e) => {
        setSettings({ ...settings, [e.target.name]: e.target.value });
    };

    const handleAnnouncementChange = (e) => {
        const { name, value, type, checked } = e.target;
        if (type === 'checkbox') {
            setAnnouncementSettings({ ...announcementSettings, [name]: checked ? 'true' : 'false' });
        } else {
            setAnnouncementSettings({ ...announcementSettings, [name]: value });
        }
    };

    const handleImageChange = (e) => {
        setImageFile(e.target.files[0]);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            setLoading(true);
            setMessage({ type: '', text: 'Saving global settings...' });
            
            await updateSettings(settings);
            
            setMessage({ type: 'success', text: 'Success! Global settings updated.' });
        } catch (error) {
            setMessage({ type: 'danger', text: `Failed: ${error.message}` });
        } finally {
            setLoading(false);
        }
    };

    const handleAnnouncementSubmit = async (e) => {
        e.preventDefault();
        try {
            setLoading(true);
            setMessage({ type: '', text: 'Saving announcement settings...' });
            
            let imageUrl = announcementSettings.announcement_image_url;
            if (imageFile) {
                setMessage({ type: '', text: 'Uploading announcement image...' });
                imageUrl = await uploadImageToR2(imageFile);
            }
            
            // Generate a new ID whenever the announcement is updated, so it reappears for users when modified
            const newId = Date.now().toString();

            const finalAnnouncementSettings = {
                ...announcementSettings,
                announcement_image_url: imageUrl,
                announcement_id: newId
            };

            await updateSettings(finalAnnouncementSettings);
            
            setAnnouncementSettings(finalAnnouncementSettings);
            setImageFile(null); // Reset image input
            setMessage({ type: 'success', text: 'Success! Announcement updated.' });
        } catch (error) {
            setMessage({ type: 'danger', text: `Failed: ${error.message}` });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="admin-gallery-container pt-5 pb-5">
            <div className="container">
                <AdminNavbar />

                {message.text && (
                    <div className={`alert alert-${message.type} alert-dismissible fade show`} role="alert">
                        {message.text}
                        <button type="button" className="close" onClick={() => setMessage({type: '', text: ''})} aria-label="Close">
                            <span aria-hidden="true">&times;</span>
                        </button>
                    </div>
                )}

                <div className="row justify-content-center">
                    <div className="col-lg-8 mb-4">
                        <div className="admin-card upload-card">
                            <h4>Global Contact & Social Settings</h4>
                            <p className="text-muted" style={{fontSize: '0.9rem'}}>Manage your public contact information. Put each new entry on a new line.</p>
                            <form onSubmit={handleSubmit}>
                                <div className="row">
                                    <div className="col-md-6 form-group mt-3">
                                        <label>Addresses (One per line)</label>
                                        <textarea className="form-control admin-input" name="addresses" value={settings.addresses} onChange={handleChange} rows="4" placeholder="Enter addresses..."></textarea>
                                    </div>
                                    <div className="col-md-6 form-group mt-3">
                                        <label>Emails (One per line)</label>
                                        <textarea className="form-control admin-input" name="emails" value={settings.emails} onChange={handleChange} rows="4" placeholder="Enter emails..."></textarea>
                                    </div>
                                    <div className="col-md-12 form-group mt-3">
                                        <label>Phone Numbers (One per line)</label>
                                        <textarea className="form-control admin-input" name="phones" value={settings.phones} onChange={handleChange} rows="3" placeholder="Enter phone numbers..."></textarea>
                                    </div>
                                </div>
                                
                                <hr className="mt-4 mb-3" />
                                <h5>Social Media Links</h5>
                                
                                <div className="form-group mt-3">
                                    <label>Facebook URL</label>
                                    <input type="text" className="form-control admin-input" name="facebook" value={settings.facebook} onChange={handleChange} placeholder="https://facebook.com/..." />
                                </div>
                                <div className="form-group mt-3">
                                    <label>Instagram URL</label>
                                    <input type="text" className="form-control admin-input" name="instagram" value={settings.instagram} onChange={handleChange} placeholder="https://instagram.com/..." />
                                </div>
                                <div className="form-group mt-3">
                                    <label>LinkedIn URL</label>
                                    <input type="text" className="form-control admin-input" name="linkedin" value={settings.linkedin} onChange={handleChange} placeholder="https://linkedin.com/..." />
                                </div>

                                <button type="submit" className="upload-btn w-100 mt-4" disabled={loading}>
                                    {loading ? 'Saving...' : 'Update Settings'}
                                </button>
                            </form>
                        </div>
                    </div>
                </div>

                <div className="row justify-content-center mt-4">
                    <div className="col-lg-8 mb-4">
                        <div className="admin-card upload-card" style={{borderTop: '4px solid #f59e0b'}}>
                            <h4 className="d-flex align-items-center">
                                <span className="mr-2" style={{fontSize: '1.5rem'}}>📢</span>
                                Popup Announcement Banner
                            </h4>
                            <p className="text-muted" style={{fontSize: '0.9rem'}}>
                                Setup a popup announcement that appears when visitors land on the website.
                                Updating this will reset the "Don't show this Next" for all users.
                            </p>
                            
                            <form onSubmit={handleAnnouncementSubmit}>
                                <div className="form-group mt-3 d-flex align-items-center bg-light p-3 rounded shadow-sm">
                                    <div className="custom-control custom-switch">
                                        <input 
                                            type="checkbox" 
                                            className="custom-control-input" 
                                            id="announcementActiveSwitch" 
                                            name="announcement_active"
                                            checked={announcementSettings.announcement_active === 'true'}
                                            onChange={handleAnnouncementChange}
                                        />
                                        <label className="custom-control-label font-weight-bold" htmlFor="announcementActiveSwitch" style={{cursor: 'pointer'}}>
                                            Enable Announcement Modal
                                        </label>
                                    </div>
                                </div>
                                
                                {announcementSettings.announcement_active === 'true' && (
                                    <div className="mt-4 p-3 border rounded shadow-sm">
                                        <div className="form-group">
                                            <label className="font-weight-bold">Announcement Title</label>
                                            <input 
                                                type="text" 
                                                className="form-control admin-input" 
                                                name="announcement_title" 
                                                value={announcementSettings.announcement_title} 
                                                onChange={handleAnnouncementChange} 
                                                placeholder="e.g., Important Update!" 
                                                required
                                            />
                                        </div>
                                        
                                        <div className="form-group mt-3">
                                            <label className="font-weight-bold">Announcement Text</label>
                                            <textarea 
                                                className="form-control admin-input" 
                                                name="announcement_text" 
                                                value={announcementSettings.announcement_text} 
                                                onChange={handleAnnouncementChange} 
                                                rows="3" 
                                                placeholder="Enter the announcement message here..."
                                                required
                                            ></textarea>
                                        </div>
                                        
                                        <div className="form-group mt-3">
                                            <label className="font-weight-bold">Announcement Image (Optional)</label>
                                            {announcementSettings.announcement_image_url && !imageFile && (
                                                <div className="mb-2">
                                                    <img src={announcementSettings.announcement_image_url} alt="Current banner" className="img-thumbnail" style={{maxHeight: '120px'}} />
                                                </div>
                                            )}
                                            <input 
                                                type="file" 
                                                className="form-control-file" 
                                                accept="image/*"
                                                onChange={handleImageChange} 
                                            />
                                        </div>
                                    </div>
                                )}
                                
                                <button type="submit" className="upload-btn w-100 mt-4" style={{background: 'linear-gradient(135deg, #f59e0b, #d97706)'}} disabled={loading}>
                                    {loading ? 'Saving...' : 'Save Announcement'}
                                </button>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
